import React, { memo, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import { isErrorRes } from 'requests/RequestBase';
import {
	GetBulkOrderResData,
	callConfirmBulkOrderApi,
	callCaptureOrderApi,
	GetSiteSettlementResData,
} from 'requests/RequestPayment';
import { GetPaymentCardResData, callGetPaymentCardApi } from 'requests/RequestCard';
import { GetSiteResData } from 'requests/RequestSite';
import { PaymentType, isPayPal, isCredit } from 'consts/PaymentConst';
import { HttpStatus } from 'consts/HttpStatus';
import { Page } from 'consts/BulkPaymentConst';
import { findErrorMessageByApiError, isCardError } from 'consts/ApiErrorCode';
import { BulkPaymentRemarks } from 'components/molecules/bulk-payment-remarks/BulkPaymentRemarks';
import classes from 'components/templates/override.module.scss';

// 注文情報内容確認画面
type ConfirmComponentProps = {
	Overview: JSX.Element;
	order: GetBulkOrderResData;
	payMethod: PaymentType;
	transErrorPage: Function;
	setPage: React.Dispatch<React.SetStateAction<number>>;
	displayCardBrandImage: (brand?: string) => JSX.Element;
	setConfirmLoading: React.Dispatch<React.SetStateAction<boolean>>;
	is_via_portal: boolean;
	redirect: (payment: PaymentType) => void;
	site: GetSiteResData;
	siteSettlement: GetSiteSettlementResData;
	redirect_uri: string;
	state?: string;
	completeUrl: string;
	setCardError: React.Dispatch<React.SetStateAction<string>>;
};
export const ConfirmComponent = memo((props: ConfirmComponentProps) => {
	const {
		Overview,
		order,
		payMethod,
		transErrorPage,
		setPage,
		displayCardBrandImage,
		setConfirmLoading,
		is_via_portal,
		redirect,
		site,
		siteSettlement,
		redirect_uri,
		state,
		completeUrl,
		setCardError,
	} = props;
	const history = useHistory();
	const [loading, setLoading] = useState(true);
	const [cardInfo, setCardInfo] = useState<GetPaymentCardResData>(undefined);
	useEffect(() => {
		if (!isCredit(payMethod)) {
			setLoading(false);
			return;
		}
		const execute = async () => {
			const result = await callGetPaymentCardApi(order.gid_order_no);
			if (result.status === HttpStatus.OK) {
				const data = result.data as GetPaymentCardResData;
				setCardInfo(data);
			}
			setLoading(false);
		};
		execute();
	}, []);

	// 注文確定する
	const confirm = async () => {
		setConfirmLoading(true);
		// 注文情報確定（一括）
		const confirmResult = await callConfirmBulkOrderApi(order.gid_order_no, { payment_type: payMethod.key });
		if (confirmResult.status !== HttpStatus.OK) {
			if (isErrorRes(confirmResult.data)) {
				const error = confirmResult.data;
				if (isCardError(error)) {
					setCardError(findErrorMessageByApiError(error));
					setPage(Page.DEFAULT);
					setConfirmLoading(false);
					return;
				}
			}
			transErrorPage();
			setConfirmLoading(false);
			return;
		}

		// オーソリ決済（即時決済の場合
		if (order.immediate_capture) {
			const captureResult = await callCaptureOrderApi(order.gid_order_no);
			if (captureResult.status !== HttpStatus.OK) {
				transErrorPage();
				setConfirmLoading(false);
				return;
			}
		}

		setConfirmLoading(false);

		if (is_via_portal) {
			history.push(completeUrl, {
				payMethod,
				site,
				siteSettlement,
				is_via_portal,
				state,
				redirect_uri,
				order,
			});
		} else {
			redirect(payMethod);
		}
	};

	const isUserDetailEmpty = !order.user_detail || order.user_detail.length === 0;

	return (
		<>
			{Overview}
			{/* ご注文者情報 */}
			<div className={classes.shippingWrap}>
				{!isUserDetailEmpty && (
					<div className={classes.shipping}>
						<div className={classes.shippingFirstTitleDiv}>
							<img className={classes.shippingTitleIcon} src={`${process.env.PUBLIC_URL}/images/User.png`} alt="" />
							<span className={classes.shippingTitle}>ご注文者情報</span>
						</div>
						<div className={classes.shippingTable}>
							{order.user_detail.map((d) => {
								return <ShippingTableRow head={d.key} col={d.value} />;
							})}
						</div>
					</div>
				)}

				<div className={classes.shipping}>
					<div className={isUserDetailEmpty ? classes.shippingFirstTitleDiv : classes.shippingTitleDiv}>
						<img className={classes.shippingTitleIcon} src={`${process.env.PUBLIC_URL}/images/card.png`} alt="" />
						<span className={classes.shippingTitle}>お支払い情報</span>
					</div>
					<div className={classes.shippingTable}>
						<ShippingTableRow head="お支払い方法" col={isPayPal(payMethod) ? `PayPal` : `クレジットカード`} />
						{isCredit(payMethod) && (
							<ShippingTableRow
								head="カード番号"
								col={
									loading ? (
										<CircularProgress size="2rem" />
									) : (
										<div className={classes.shippingCardInfo}>
											{displayCardBrandImage(cardInfo.brand)}
											{`**** **** **** ${cardInfo.last_digits}`}
										</div>
									)
								}
							/>
						)}
					</div>
				</div>
				{order.shipping_method && order.shipping_method.length > 0 && (
					<div className={classes.shipping}>
						<div className={classes.shippingTitleDiv}>
							<img className={classes.shippingTitleIcon} src={`${process.env.PUBLIC_URL}/images/truck.png`} alt="" />
							<span className={classes.shippingTitle}>配送方法</span>
						</div>
						<div className={classes.shippingTable}>
							{order.shipping_method.map((s) => {
								return <ShippingTableRow head={s.key} col={s.value} />;
							})}
						</div>
					</div>
				)}
			</div>
			{/* 確認事項 */}
			<BulkPaymentRemarks title="ご注文確定前に再度以下をご確認ください" remarks={order.remarks} />
			{/* 各種リンク */}
			{order.various_links && order.various_links.length > 0 && (
				<div className={classes.paymentLinkWrap}>
					<div className={classes.paymentLinkTitle}>◆各種リンク</div>
					<div className={classes.paymentLink}>
						{order.various_links.map((l) => {
							return (
								<div>
									<a href={l.url} target="_blank">
										{l.key}
									</a>
								</div>
							);
						})}
					</div>
				</div>
			)}
			{/* 操作ボタン */}
			<div className={classes.sectionPaymentInfoRow}>
				<div className="btn-box">
					<div className="btn wh">
						<button
							onClick={() => {
								setPage(Page.DEFAULT);
							}}
						>
							戻る
						</button>
					</div>
					<div className="btn gr">
						<button onClick={confirm}>注文する</button>
					</div>
				</div>
			</div>
		</>
	);
});

type ShippingTableRowProps = {
	head: string;
	col: string | JSX.Element;
};
const ShippingTableRow = (props: ShippingTableRowProps) => {
	const { head, col } = props;
	return (
		<div className={classes.shippingTableRow}>
			<div className={classes.shippingTableColHead}>{head}</div>
			<div className={classes.shippingTableCol}>{col}</div>
		</div>
	);
};
